import { useEffect, useMemo, useState } from 'react';
import { formatDistanceToNow } from "date-fns";
import {
  Badge,
  Box,
  Button,
  Chip,
  ClickAwayListener,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { keyframes } from '@mui/system';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import Iconify from '../../../reusable-components/iconify';
import useInterval from '../../../reusable-components/use-interval/UseInterval';
import { useNotificationMetricAccess, withNotificationFilter } from '../../../hooks/useNotificationIdsFilter';
import { useDispatch, useSelector } from '../../../redux/store';
import {
  fetchNotifications,
  NotificationMetricType,
  pollNotificationCount,
  selectAdjustedMetrics,
  setHasNewNotifications,
} from '../../../redux/slices/notifications';

const haloPulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(211, 47, 47, 0.6); }
  70% { box-shadow: 0 0 0 8px rgba(211, 47, 47, 0); }
  100% { box-shadow: 0 0 0 0 rgba(211, 47, 47, 0); }
`;

export default function NotificationsSummary() {
  const user = useSelector((state) => state.user.user);
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openNeedsAttentionSummary, setOpenNeedsAttentionSummary] = useState(false);
  const [openOrderMetrics, setOpenOrderMetrics] = useState(false);
  const [hasShownLoginModal, setHasShownLoginModal] = useState(searchParams.get('autoDisplayNotifications') !== "true");
  const [activeFilter, setActiveFilter] = useState('all');
  const {
    notificationsData,
    isLoading: isNotificationsLoading,
    hasNewNotifications,
  } = useSelector((state) => state.notifications);
  const hasAccess = useNotificationMetricAccess();

  useEffect(() => {
    if (user?.id) {
      dispatch(fetchNotifications());
    }
  }, [user?.id, dispatch]);

  useInterval(() => {
    if (user?.id) dispatch(pollNotificationCount());
  }, 60000);

  useEffect(() => {
    if (!hasNewNotifications) return undefined;
    const timeoutId = setTimeout(() => dispatch(setHasNewNotifications(false)), 2400);
    return () => clearTimeout(timeoutId);
  }, [hasNewNotifications, dispatch]);

  const now = new Date();
  const hour = now.getHours();

  let greeting;

  if (hour < 12) {
    greeting = `Good Morning, ${user.firstName}!`;
  } else if (hour < 18) {
    greeting = `Good Afternoon, ${user.firstName}!`;
  } else {
    greeting = `Good Evening, ${user.firstName}!`;
  }

  useEffect(() => {
    const hasSeenModalThisSession = sessionStorage.getItem('hasSeenLoginModal') === 'true';
    
    if (
      user?.id &&
      !hasSeenModalThisSession &&
      !hasShownLoginModal &&
      !isNotificationsLoading &&
      notificationsData &&
      (Object.prototype.hasOwnProperty.call(notificationsData, 'orderMetrics') ||
        Object.prototype.hasOwnProperty.call(notificationsData, 'invoiceMetrics'))
    ) {
      setOpenNeedsAttentionSummary(false);
      setOpenOrderMetrics(true);
      setHasShownLoginModal(true);
      setSearchParams(prev => { prev.delete("autoDisplayNotifications"); return prev; });
      sessionStorage.setItem('hasSeenLoginModal', 'true');
    }
  }, [
    user?.id,
    hasShownLoginModal,
    isNotificationsLoading,
    notificationsData,
]);

  const adjustedMetrics = useSelector(selectAdjustedMetrics);

  const respondedUnreadTickets = adjustedMetrics[NotificationMetricType.TicketsUnread].items;
  const awaitingApprovalOrders = adjustedMetrics[NotificationMetricType.OrdersNeedApproval].items;
  const submittedAwaitingApprovalOrders = adjustedMetrics[NotificationMetricType.OrdersSubmitted].items;
  const awaitingApprovalInvoices = adjustedMetrics[NotificationMetricType.InvoicesNeedApproval].items;
  const submittedAwaitingApprovalInvoices = adjustedMetrics[NotificationMetricType.InvoicesSubmitted].items;
  const needsApprovalRentals = adjustedMetrics[NotificationMetricType.RentalsNeedApproval].items;
  const submittedAwaitingApprovalRentals = adjustedMetrics[NotificationMetricType.RentalsSubmitted].items;

  const canApproveOrders = hasAccess(NotificationMetricType.OrdersNeedApproval);
  const canCreateOrders = hasAccess(NotificationMetricType.OrdersSubmitted);
  const canApproveInvoices = hasAccess(NotificationMetricType.InvoicesNeedApproval);
  const canSubmitInvoices = hasAccess(NotificationMetricType.InvoicesSubmitted);
  const canApproveRentals = hasAccess(NotificationMetricType.RentalsNeedApproval);
  const canCreateRentals = hasAccess(NotificationMetricType.RentalsSubmitted);

  const ordersNeedApprovalCount = canApproveOrders ? adjustedMetrics.ordersNeedApproval.count : 0;
  const ordersAwaitingApprovalCount = canCreateOrders ? adjustedMetrics.ordersSubmitted.count : 0;
  const invoicesNeedApprovalCount = canApproveInvoices ? adjustedMetrics.invoicesNeedApproval.count : 0;
  const invoicesAwaitingSubmissionCount = canSubmitInvoices ? adjustedMetrics.invoicesSubmitted.count : 0;
  const rentalsNeedApprovalCount = canApproveRentals ? adjustedMetrics.rentalsNeedApproval.count : 0;
  const rentalsAwaitingApprovalCount = canCreateRentals ? adjustedMetrics.rentalsSubmitted.count : 0;
  const rentalsNeedsAttentionCount = rentalsNeedApprovalCount + rentalsAwaitingApprovalCount;
  const ticketsUnreadCount = adjustedMetrics.ticketsUnread.count;

  const visibleMetricItems = useMemo(
    () =>
      [
        {
          type: 'tickets',
          label: 'Tickets',
          summaryDescription: 'Waiting to be viewed',
          dialogDescription: 'Need review',
          value: ticketsUnreadCount,
          color: '#2B7FC7',
          link: withNotificationFilter('/dashboard/tickets?status=active', ticketsUnreadCount),
          visible: true,
        },
        {
          type: 'orders',
          label: 'Orders',
          summaryDescription: 'Need approval',
          dialogDescription: 'Need approval',
          value: ordersNeedApprovalCount + ordersAwaitingApprovalCount,
          color: '#2B7FC7',
          link: withNotificationFilter('/dashboard/orders?filter=pending', ordersNeedApprovalCount + ordersAwaitingApprovalCount),
          visible: canApproveOrders || canCreateOrders,
        },
        {
          type: 'invoices',
          label: 'Invoices',
          summaryDescription: 'Need approval',
          dialogDescription: 'Need approval',
          value: invoicesNeedApprovalCount + invoicesAwaitingSubmissionCount,
          color: '#2B7FC7',
          link: invoicesNeedApprovalCount + invoicesAwaitingSubmissionCount
            ? withNotificationFilter('/dashboard/invoicing/pending', invoicesNeedApprovalCount + invoicesAwaitingSubmissionCount)
            : '/dashboard/invoicing/pendingFilter',
          visible: canApproveInvoices || canSubmitInvoices,
        },
        {
          type: 'rentals',
          label: 'Rentals',
          summaryDescription: 'Pending confirmation',
          dialogDescription: 'Pending Confirmation',
          value: rentalsNeedsAttentionCount,
          color: '#2B7FC7',
          link: withNotificationFilter('/dashboard/rentals/pending-rentals', rentalsNeedsAttentionCount),
          visible: canApproveRentals || canCreateRentals,
        },
      ].filter((item) => item.visible),
    [
      ticketsUnreadCount,
      ordersNeedApprovalCount,
      ordersAwaitingApprovalCount,
      invoicesNeedApprovalCount,
      invoicesAwaitingSubmissionCount,
      rentalsNeedsAttentionCount,
      canApproveOrders,
      canCreateOrders,
      canApproveInvoices,
      canSubmitInvoices,
      canApproveRentals,
      canCreateRentals,
    ]
  );

  const visibleMetricTypes = useMemo(
    () => new Set(visibleMetricItems.map((item) => item.type)),
    [visibleMetricItems]
  );

  const needsAttentionCount = useMemo(
    () => visibleMetricItems.reduce((total, item) => total + item.value, 0),
    [visibleMetricItems]
  );

  const notifications = useMemo(() => {
    const orders = awaitingApprovalOrders.map((order) => ({
      id: `order-${order.id}`,
      type: 'orders',
      title: `Order #${order.orderNumber || order.id}`,
      subtitle: `${order.facilityShortName || 'Unknown'} - ${order.department || 'Awaiting approval'}`,
      link: `/dashboard/orders/${order.id}?filter=pending`,
      time: formatDistanceToNow(new Date(order.time)) || 'Now',
    }));

    const submittedOrders = submittedAwaitingApprovalOrders.map((order) => ({
      id: `submitted-order-${order.id}`,
      type: 'orders',
      title: `Order #${order.orderNumber || order.id}`,
      subtitle: `${order.facilityShortName || 'Unknown'} - ${order.department || 'Awaiting someone else\'s approval'}`,
      link: `/dashboard/orders/${order.id}?filter=pending`,
      time: formatDistanceToNow(new Date(order.time)) || 'Now',
    }));

    const invoices = awaitingApprovalInvoices.map((invoice) => ({
      id: `invoice-${invoice.id}`,
      type: 'invoices',
      title: `Invoice #${invoice.invoiceId || invoice.id}`,
      subtitle: `${invoice.vendor || 'Unknown'} - ${invoice.facility || 'Awaiting approval'}`,
      link: `/dashboard/invoicing/pendingFilter/${invoice.id}`,
      time: formatDistanceToNow(new Date(invoice.time)) || 'Now',
    }));

    const submittedInvoices = submittedAwaitingApprovalInvoices.map((invoice) => ({
      id: `submitted-invoice-${invoice.id}`,
      type: 'invoices',
      title: `Invoice #${invoice.invoiceId || invoice.id}`,
      subtitle: `${invoice.vendor || 'Unknown'} - ${invoice.facility || 'Awaiting someone else\'s approval'}`,
      link: `/dashboard/invoicing/pendingFilter/${invoice.id}`,
      time: formatDistanceToNow(new Date(invoice.time)) || 'Now',
    }));

    const tickets = respondedUnreadTickets.map((ticket) => ({
      id: `ticket-${ticket.id}`,
      type: 'tickets',
      title: `Ticket #${ticket.id}`,
      subtitle: `${ticket.facility || 'Unknown'} - ${ticket.extraInfo || 'Customer replied'}`,
      link: `/dashboard/tickets/${ticket.id}`,
      time: formatDistanceToNow(new Date(ticket.time)) || 'Now',
    }));

    const rentals = needsApprovalRentals.map((rental) => ({
      id: `rental-${rental.id}`,
      type: 'rentals',
      title: `Rental #${rental.orderNumber || rental.id}`,
      subtitle: 'Need approval',
      link: `/dashboard/rentals/rental-order/${rental.id}/pending`,
      time: formatDistanceToNow(new Date(rental.time)) || 'Now',
    })) ?? [];

    const submittedRentals = submittedAwaitingApprovalRentals.map((rental) => ({
      id: `rental-${rental.id}`,
      type: 'rentals',
      title: `Rental #${rental.orderNumber || rental.id}`,
      subtitle: 'Awaiting approval',
      link: `/dashboard/rentals/rental-order/${rental.id}/pending`,
      time: formatDistanceToNow(new Date(rental.time)) || 'Now',
    })) ?? [];

    return [
      ...(canApproveOrders ? orders : []),
      ...(canCreateOrders ? submittedOrders : []),
      ...(canApproveInvoices ? invoices : []),
      ...(canSubmitInvoices ? submittedInvoices : []),
      ...tickets,
      ...(canApproveRentals ? rentals : []),
      ...(canCreateRentals ? submittedRentals : []),
    ];
  }, [
    awaitingApprovalOrders,
    submittedAwaitingApprovalOrders,
    awaitingApprovalInvoices,
    submittedAwaitingApprovalInvoices,
    respondedUnreadTickets,
    needsApprovalRentals,
    submittedAwaitingApprovalRentals,
    canApproveOrders,
    canCreateOrders,
    canApproveInvoices,
    canSubmitInvoices,
    canApproveRentals,
    canCreateRentals,
  ]);

  useEffect(() => {
    if (activeFilter !== 'all' && !visibleMetricTypes.has(activeFilter)) {
      setActiveFilter('all');
    }
  }, [activeFilter, visibleMetricTypes]);

  const filteredNotifications = useMemo(() => {
    const scopeFilteredNotifications = notifications.filter((notification) =>
      visibleMetricTypes.has(notification.type)
    );

    if (activeFilter === 'all') {
      return scopeFilteredNotifications;
    }

    return scopeFilteredNotifications.filter(
      (notification) => notification.type === activeFilter
    );
  }, [activeFilter, notifications, visibleMetricTypes]);

  const handleNotificationClick = () => {
    setOpenOrderMetrics(false);
  };

  const handleSummaryClick = () => {
    setOpenNeedsAttentionSummary(false);
    setOpenOrderMetrics(false);
  }

  const handleOpenNeedsAttentionSummary = () => {
    if (!openNeedsAttentionSummary) {
      dispatch(fetchNotifications());
    }
    dispatch(setHasNewNotifications(false));
    setOpenNeedsAttentionSummary((previous) => !previous);
  };

  const handleOpenOrderMetrics = () => {
    dispatch(fetchNotifications());
    dispatch(setHasNewNotifications(false));
    setOpenNeedsAttentionSummary(false);
    setOpenOrderMetrics(true);
    setSearchParams(prev => { prev.delete("autoDisplayNotifications"); return prev; });
  };

  const handleCloseOrderMetrics = () => {
    setOpenOrderMetrics(false);
  };

  return (
    <>
      <ClickAwayListener onClickAway={() => setOpenNeedsAttentionSummary(false)}>
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <Button
            color="primary"
            onClick={handleOpenNeedsAttentionSummary}
            size="small"
            sx={{ minWidth: 0 }}
          >
            <Badge
              badgeContent={needsAttentionCount}
              color="error"
              anchorOrigin={{ horizontal: "left", vertical: "top" }}
              componentsProps={{
                badge: {
                  sx: {
                    animation: hasNewNotifications ? `${haloPulse} 1.2s ease-out 2` : 'none',
                  },
                },
              }}
            >
              <Iconify icon="eva:bell-outline" width={24} height={24} />
            </Badge>
          </Button>

          {openNeedsAttentionSummary && (
        <Paper
          elevation={2}
          sx={{
            p: 2,
            borderRadius: 2,
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            zIndex: (theme) => theme.zIndex.modal + 1,
            minWidth: 360,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1.5 }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ color: '#99C455' }}
              >
                Needs Attention
              </Typography>
              <Badge
                color="error"
                variant="dot"
                invisible={needsAttentionCount === 0}
              >
                <Iconify icon="eva:bell-outline" width={18} />
              </Badge>
            </Stack>

            <Button
              size="small"
              onClick={handleOpenOrderMetrics}
              endIcon={<ChevronRightIcon fontSize="small" />}
              sx={{ color: '#99C455', fontWeight: 600 }}
            >
              View All
            </Button>
          </Stack>

          <Paper>
            <List disablePadding>
              {visibleMetricItems.map((item, index, arr) => (
                <ListItemButton
                  key={item.label}
                  component={RouterLink}
                  to={item.link}
                  onClick={handleSummaryClick}
                  sx={{
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                > 
                <Grid container>
                  <Grid item>
                    <Typography
                      // variant="h4"
                      sx={{ minWidth: 52, color: item.color, lineHeight: 1, fontSize: '36px', fontWeight: 600 }}
                    >
                      {item.value}
                    </Typography>
                  </Grid>
                  
                  <Grid item>
                    <Typography variant="subtitle2" display="block">{item.label}</Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {item.summaryDescription}
                    </Typography>
                  </Grid>
                </Grid>
                  
                  <ChevronRightIcon fontSize="small" />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Paper>
      )}
        </Box>
      </ClickAwayListener>

      <Dialog
        open={openOrderMetrics}
        onClose={handleCloseOrderMetrics}
        PaperProps={{
          sx: {
            overflow: 'hidden',
            maxHeight: '85vh',
            width: '800px'
          },
        }}
      >
        <DialogContent
          sx={{
            p: 3,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 3,
            }}
          >
            <Box>
              <Typography
                variant="h5"
                fontWeight={700}
              >
                {greeting}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Here's what needs your attention today.
              </Typography>
            </Box>

            <IconButton
              onClick={handleCloseOrderMetrics}
            >
              <Iconify icon="eva:close-fill" />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 1,
              mb: 3,
            }}
          >
            {visibleMetricItems.map((item) => (
              <Paper
                key={item.type}
                component={RouterLink}
                to={item.link}
                onClick={handleCloseOrderMetrics}
                variant="outlined"
                sx={{
                  flex: 1,
                  py: 1.5,
                  textAlign: 'center',
                  color: 'inherit',
                  textDecoration: 'none',
                }}
              >
                <Typography
                  variant="h4"
                  fontWeight={700}
                  color={item.color}
                  lineHeight={1}
                >
                  {item.value}
                </Typography>

                <Typography
                  variant="subtitle2"
                  display="block"
                >
                  {item.label}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {item.dialogDescription}
                </Typography>
              </Paper>
            ))}
          </Box>

          <Typography
            variant="subtitle1"
            fontWeight={700}
            sx={{ mb: 2 }}
          >
            Action Required
          </Typography>

          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            sx={{ mb: 2 }}
          > 
            <Chip
              label={`All (${needsAttentionCount})`}
              sx={{
              backgroundColor:
                activeFilter === 'all'
                  ? '#99C455'
                  : 'transparent',
              borderRadius: 6,
              border: '1px solid var(--Background-Secondary-Hover, #E6E6E6)',
            }}
              onClick={() =>
                setActiveFilter('all')
              }
            />

            {visibleMetricItems.map((item) => (
              <Chip
                key={item.type}
                label={`${item.label} (${item.value})`}
                sx={{
                  backgroundColor:
                    activeFilter === item.type
                      ? '#99C455'
                      : 'transparent',
                  borderRadius: 6,
                  border: '1px solid var(--Background-Secondary-Hover, #E6E6E6)',
                }}
                onClick={() =>
                  setActiveFilter(item.type)
                }
              />
            ))}
          </Stack>

          <List 
          disablePadding
            sx={{
              maxHeight: 340,
              overflowY: 'auto',
            }}
          >
            {filteredNotifications.map(
              (notification) => (
                <ListItemButton
                  key={notification.id}
                  component={RouterLink}
                  to={notification.link}
                  onClick={handleNotificationClick}
                  sx={{
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                      >
                        {notification.title}
                      </Typography>
                    }
                    secondary={
                      notification.subtitle
                    }
                    secondaryTypographyProps={{
                      noWrap: true,
                      sx: {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },
                    }}
                  />
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      {notification.time}
                    </Typography>

                    <ChevronRightIcon
                      fontSize="small"
                    />
                  </Stack>
                </ListItemButton>
              )
            )}

            {filteredNotifications.length === 0 && (
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  textAlign: 'center',
                }}
              >
                <Typography
                  color="text.secondary"
                >
                  You're all caught up.
                </Typography>
              </Paper>
            )}
          </List>
        </DialogContent>
      </Dialog>
    </>
  );
}